import { useState, useRef } from "react";
import { motion } from "motion/react";
import type { CommunityPost } from "@/hooks/useCommunity";

interface BestReviewSliderProps {
  posts: CommunityPost[];
  onPostClick: (id: string) => void;
}

export default function BestReviewSlider({ posts, onPostClick }: BestReviewSliderProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const cardWidth = 340;
    const gap = 16;
    const scrollAmount = cardWidth + gap;
    const newIndex = direction === "left"
      ? Math.max(0, currentIndex - 1)
      : Math.min(posts.length - 1, currentIndex + 1);
    setCurrentIndex(newIndex);
    scrollRef.current.scrollTo({ left: newIndex * scrollAmount, behavior: "smooth" });
  };

  const badgeColor = (badge?: string) => {
    if (badge === "마스터") return "bg-amber-50 text-amber-700 border-amber-200";
    if (badge === "정수강생") return "bg-emerald-50 text-emerald-600 border-emerald-200";
    return "";
  };

  if (posts.length === 0) return null;

  return (
    <div className="relative mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-lg">🔥</span>
          <h2 className="text-base font-bold text-foreground-950">베스트 임장/낙찰 후기</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scroll("left")}
            disabled={currentIndex === 0}
            className="w-8 h-8 rounded-full border border-foreground-200 flex items-center justify-center hover:bg-background-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          <button
            onClick={() => scroll("right")}
            disabled={currentIndex >= posts.length - 1}
            className="w-8 h-8 rounded-full border border-foreground-200 flex items-center justify-center hover:bg-background-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-1"
        style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }}
      >
        {posts.map((post, idx) => (
          <motion.button
            key={post.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.08, duration: 0.35 }}
            onClick={() => onPostClick(post.id)}
            className="group shrink-0 w-[300px] md:w-[340px] text-left rounded-2xl overflow-hidden border border-foreground-200 hover:border-accent-200 transition-all cursor-pointer"
            style={{ scrollSnapAlign: "start" }}
          >
            <div className="relative h-40 md:h-44 overflow-hidden">
              {post.thumbnail ? (
                <img loading="lazy" decoding="async"
                  src={post.thumbnail}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full bg-background-100 flex items-center justify-center">
                  <i className="ri-image-line text-3xl text-foreground-300"></i>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
              <div className="absolute bottom-3 left-3 right-3">
                <div className="flex items-center gap-1.5 text-white/90 text-xs">
                  <i className="ri-eye-line"></i>
                  <span>{post.views.toLocaleString()}</span>
                  <span className="mx-1">·</span>
                  <i className="ri-heart-line"></i>
                  <span>{post.likes}</span>
                  <span className="mx-1">·</span>
                  <i className="ri-chat-3-line"></i>
                  <span>{post.commentCount}</span>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                {post.authorBadge && (
                  <span className={`shrink-0 px-2 py-0.5 text-[11px] font-medium rounded-full border ${badgeColor(post.authorBadge)}`}>
                    {post.authorBadge}
                  </span>
                )}
                <span className="text-xs text-foreground-500 whitespace-nowrap">{post.author}</span>
                <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">{post.createdAt}</span>
              </div>
              <h3 className="text-sm font-bold text-foreground-950 line-clamp-2 leading-snug">{post.title}</h3>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}